import { useNavigate } from 'react-router';

import { IconLanguage, IconPrinter, IconShare2 } from '@tabler/icons-react';
import { useAtomValue, useSetAtom } from 'jotai';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { printTridionDocument } from '@/utils/print';
import { cn } from '@/utils/tailwind';

import { Header } from '@/components/header';
import ToastMessageBlock from '@/components/Toasts/SuccessToast';

import { routes } from '@/constants';

import {
  documentPersistedAtom,
  translatedDocumentNameAtom,
} from '@/atoms/document-persisted';
import { toggleTranslationAtom } from '@/atoms/document-transient';
import { searchAtom } from '@/atoms/search';

interface DocumentHeaderProps {
  name?: string;
  planName?: string;
  planId?: string;
  isTrainingMode?: boolean;
  isTridion?: boolean;
}

const DocumentHeader: React.FC<DocumentHeaderProps> = ({
  name,
  planName,
  planId,
  isTrainingMode,
  isTridion: isTridionProp,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { terms } = useAtomValue(searchAtom);
  const searchTerm = terms[0];
  const {
    hasTranslatedValue,
    routedFrom,
    categoryName,
    backUrl,
    isTridion: isTridionPersisted,
  } = useAtomValue(documentPersistedAtom);
  const toggleTranslation = useSetAtom(toggleTranslationAtom);
  const docName = useAtomValue(translatedDocumentNameAtom);

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.custom(toastObj => (
      <ToastMessageBlock id={toastObj.id}>
        {t('Generic.linkCopied')}
      </ToastMessageBlock>
    ));
  };

  const isTridion = !!isTridionProp || isTridionPersisted;

  const isSearch = routedFrom === 'search';

  const goBack = () => {
    if (isTrainingMode) {
      navigate(-1);
    } else if (planId) {
      navigate(routes.plans.plan({ planId }));
    } else if (routedFrom === 'link' && !isSearch) {
      navigate('/');
    } else if (!categoryName && searchTerm) {
      navigate(`/search?query=${searchTerm}`);
    } else if (backUrl) {
      navigate(backUrl);
    } else {
      navigate(-1);
    }
  };

  return (
    <Header.Root className={cn(isTrainingMode && 'top-0! left-0! m-2!')}>
      <Header.Title>{docName ?? name}</Header.Title>
      <Header.Back as="button" onClick={goBack}>
        {planName ??
          (routedFrom === 'link' && !isSearch
            ? t('Generic.explore')
            : categoryName
              ? categoryName
              : t('Generic.search'))}
      </Header.Back>
      {isTridion && !isTrainingMode && (
        <Header.ActionIconButton
          icon={IconLanguage}
          disabled={!hasTranslatedValue}
          tooltip="Generic.translate"
          onClick={toggleTranslation}
        />
      )}
      {isTridion && !isTrainingMode && (
        <Header.ActionIconButton
          icon={IconPrinter}
          tooltip="Button.print"
          onClick={() => printTridionDocument()}
        />
      )}
      {isTridion && !isTrainingMode && (
        <Header.ActionIconButton
          icon={IconShare2}
          tooltip="Button.copy"
          onClick={copyLink}
        />
      )}
    </Header.Root>
  );
};

export default DocumentHeader;
